import { CHANNEL } from './model';
import { fromFridgeEvents } from './adapters/fridgeEvents';
import { fromHeartbeat } from './adapters/heartbeat';
import { fromDriverActions } from './adapters/driverActions';
import { fromHealthSnapshot } from './adapters/healthSnapshot';

/**
 * Merge the sources, then split them by how they are read. Pure and deterministic.
 * Door/safety beats come from `heartbeat` OR `healthSnapshot`, never both.
 *
 * Splitting here rather than at each use site is the point: temperature is ~98% of
 * a week's records and belongs to a chart, not a list. While both lived in one
 * array every consumer had to re-derive that with `type !== 'TEMP'`, and a filter
 * meant for rows could silently take the chart with it.
 *
 * @returns {{beats: Object[], series: Object<string, Object[]>}} `beats` ascending
 *   by ts; `series` keyed by event type, each an ascending list of
 *   `{ts, value, raw}` — `value` null for a sample that carried no reading, so the
 *   chart can report what it could not plot instead of dropping it unremarked.
 */
export function buildTimeline(
  {
    fridgeEvents = [],
    heartbeat = [],
    driverActions = [],
    healthSnapshot = null,
  } = {},
  opts = {},
) {
  const merged = [
    ...fromFridgeEvents(fridgeEvents, opts.fridgeEvents),
    ...fromHeartbeat(heartbeat),
    ...fromDriverActions(driverActions),
    ...fromHealthSnapshot(healthSnapshot),
  ];

  // Tie-broken so equal-timestamp beats always render in a stable order.
  merged.sort(
    (a, b) =>
      a.ts - b.ts ||
      a.source.localeCompare(b.source) ||
      a.type.localeCompare(b.type),
  );

  // Drop exact-duplicate ids (same source+type+ts+payload — see makeEvent).
  const seen = new Set();
  const unique = merged.filter((e) =>
    seen.has(e.id) ? false : (seen.add(e.id), true),
  );

  const beats = unique.filter((e) => e.channel !== CHANNEL.SERIES);
  const series = unique
    .filter((e) => e.channel === CHANNEL.SERIES)
    .reduce((acc, e) => {
      if (!acc[e.type]) acc[e.type] = [];
      acc[e.type].push({ ts: e.ts, value: e.value, raw: e.raw });
      return acc;
    }, {});

  return { beats, series };
}
